import flow from 'lodash/flow';
import memoizeOne from 'memoize-one';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsSubject } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import { AnalyticsEventToProps } from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { Capability } from '../../common/capability/index.tsx';
import { CAN_CREATE_COLUMNS } from '../../model/permission/permission-types.tsx';
import { NO_SWIMLANE } from '../../model/swimlane/swimlane-modes.tsx';
import { preventInlineEditingAction } from '../../state/actions/board/prevent-inline-editing/index.tsx';
import { boardNonCriticalView } from '../../state/actions/work/index.tsx';
import { getPermissionsSelector } from '../../state/selectors/board/board-permissions-selectors.tsx';
import {
	getPreventInlineEditing,
	shouldRenderSwimlaneSelector,
} from '../../state/selectors/board/board-selectors.tsx';
import { getCapability } from '../../state/selectors/capability/capability-selectors.tsx';
import {
	getIsCMPBoard,
	getLocale,
	sessionIdSelector,
	getIsIncrementPlanningBoard,
	projectTypeSelector,
} from '../../state/selectors/software/software-selectors.tsx';
import { isSprintsEnabled } from '../../state/selectors/sprint/sprint-selectors.tsx';
import { getSwimlaneMode } from '../../state/selectors/swimlane/swimlane-mode-selectors.tsx';
import {
	getAssigneeFilter,
	getIsUnscheduledColumnVisible,
	isBoardConfigLoaded,
	isFilterRefinementEnabled,
	isInlineColumnEditEnabled,
} from '../../state/selectors/work/work-selectors.tsx';
import type { Dispatch, State } from '../../state/types.tsx';
import BacklogIssueMoveDialog from './board/backlog-issue-move-dialog/index.tsx';
import ColumnConfigurationButton from './board/column-configuration/index.tsx';
import ColumnCreate from './board/column-create/index.tsx';
import Board from './board/index.tsx';
import KeyboardShortcuts from './board/keyboard-shortcuts/index.tsx';
import BoardMinimap from './board/minimap/index.tsx';
import ScrollObserver from './scroll-observer/index.tsx';
import SubmitApdex from './submit-apdex/index.tsx';
import BoardContainer from './view.tsx';

type OwnProps = {
	isCacheHit: boolean;
	onDragEnd?: () => void;
};

export const mapStateToProps = (state: State) => {
	const permissions = getPermissionsSelector(state);
	const hasSwimlanes = getSwimlaneMode(state) !== NO_SWIMLANE.id;
	const boardSessionId = sessionIdSelector(state);
	const isNonCriticalWorksetLoaded = isBoardConfigLoaded(state);
	const locale = getLocale(state);
	const preventInlineEditing = getPreventInlineEditing(state);
	const isIncrementPlanningBoard = getIsIncrementPlanningBoard(state);
	const isUnscheduledColumnVisible = isIncrementPlanningBoard
		? getIsUnscheduledColumnVisible(state)
		: undefined;
	const isCMPBoard = getIsCMPBoard(state);
	// This hides the ColumnConfiguration button for CMP
	const isColumnConfigurationButtonEnabled = !isCMPBoard;
	const isJSWBoard = projectTypeSelector(state) === SOFTWARE_PROJECT;
	const hasAssigneeFilter = !!getAssigneeFilter(state);

	return {
		isColumnCreateAllowed: permissions[CAN_CREATE_COLUMNS] || false,
		isInlineColumnEditEnabled: isInlineColumnEditEnabled(state),
		isFlexibleColumns: getCapability(state)(Capability.FLEXIBLE_BOARD_COLUMNS),
		hasSwimlanes,
		isNonCriticalWorksetLoaded,
		boardSessionId,
		locale,
		preventInlineEditing,
		shouldRenderSwimlane: shouldRenderSwimlaneSelector(state),
		isSprintsEnabled: isSprintsEnabled(state),
		isIncrementPlanningBoard,
		isColumnConfigurationButtonEnabled,
		isUnscheduledColumnVisible,
		isJSWBoard,
		hasAssigneeFilter,
		isFilterRefinementEnabled: isFilterRefinementEnabled(state),
	};
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
	dispatch,
});

const onBoardNonCriticalViewedFactory = memoizeOne(
	(dispatch: Dispatch) => (analyticsEvent: UIAnalyticsEvent) =>
		// @ts-expect-error - Argument of type 'BoardNonCriticalViewAction' is not assignable to parameter of type 'Action'.
		dispatch(boardNonCriticalView(analyticsEvent)),
);
const onPreventInlineEditingFactory = memoizeOne(
	(dispatch: Dispatch) => (shouldPreventInlineEditing: boolean) =>
		dispatch(preventInlineEditingAction(shouldPreventInlineEditing)),
);

const mergeProps = (
	stateProps: ReturnType<typeof mapStateToProps>,
	dispatchProps: ReturnType<typeof mapDispatchToProps>,
	ownProps: OwnProps,
) => {
	const { isCacheHit } = ownProps;

	return {
		...ownProps,
		...stateProps,
		isCacheHit,
		Board,
		BacklogIssueMoveDialog,
		KeyboardShortcuts,
		ColumnCreate,
		ColumnConfigurationButton,
		BoardMinimap,
		SubmitApdex,
		ScrollObserver,
		onBoardNonCriticalViewed: onBoardNonCriticalViewedFactory(dispatchProps.dispatch),
		onPreventInlineEditing: onPreventInlineEditingFactory(dispatchProps.dispatch),
	};
};

const connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

export default flow(
	AnalyticsEventToProps('board', {
		onBoardNonCriticalViewed: 'viewed',
	}),
	connector,
	AnalyticsSubject('boardNonCritical'),
)(BoardContainer);
